import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { RotateCcw, Package, CheckCircle2, XCircle, Truck, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const STATUS_PILLS = {
    requested: { label: 'Requested', cls: 'pill-blue', icon: Clock },
    approved: { label: 'Approved', cls: 'pill-yellow', icon: CheckCircle2 },
    rejected: { label: 'Rejected', cls: 'pill-orange', icon: XCircle },
    pickup_scheduled: { label: 'Pickup scheduled', cls: 'pill-yellow', icon: Truck },
    received: { label: 'Received', cls: 'pill-yellow', icon: Package },
    refunded: { label: 'Refunded', cls: 'pill-yellow', icon: CheckCircle2 },
};

export default function Returns() {
    const { type, loading } = useAuth();
    const [list, setList] = useState([]);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        if (type !== 'customer') return;
        api.get('/customer/returns').then(({ data }) => setList(data)).catch(() => {}).finally(() => setFetching(false));
    }, [type]);

    if (loading || fetching) return <div className="min-h-[60vh] flex items-center justify-center">Loading…</div>;
    if (type !== 'customer') return <Navigate to="/login?next=/account/returns" replace />;

    return (
        <div className="bg-cream min-h-screen" data-testid="returns-page">
            <section className="bg-inorbit-blue-deeper text-white pt-28 pb-10">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <Link to="/profile" className="text-xs uppercase tracking-widest text-inorbit-yellow hover:underline">← Account</Link>
                    <h1 className="font-serif text-4xl md:text-5xl mt-3" style={{ color: 'white' }}>Returns & refunds</h1>
                    <p className="text-white/75 text-sm mt-2">Track every active return at a glance.</p>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
                {list.length === 0 ? (
                    <div className="card-luxury p-12 text-center" data-testid="returns-empty">
                        <RotateCcw size={42} className="text-inorbit-orange mx-auto mb-3" strokeWidth={1.2} />
                        <div className="font-serif text-2xl text-inorbit-blue mb-2">No returns yet</div>
                        <p className="text-text-secondary text-sm mb-4 max-w-md mx-auto">
                            Once an order is delivered, you can request a return from the order detail page within 7 days.
                        </p>
                        <Link to="/orders" className="btn-primary mx-auto inline-flex">View my orders</Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {list.map((r) => {
                            const meta = STATUS_PILLS[r.status] || STATUS_PILLS.requested;
                            const Icon = meta.icon;
                            return (
                                <div key={r.id} className="card-luxury p-6" data-testid={`return-card-${r.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <div className="overline">RMA · {r.rma_number}</div>
                                            <div className="font-serif text-xl text-inorbit-blue mt-1">Return for order #{r.order_number}</div>
                                            <div className="text-xs text-text-tertiary mt-1">{r.brand_name} · raised {new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <span className={`pill ${meta.cls} flex items-center gap-1`}><Icon size={11} /> {meta.label}</span>
                                            <div className="font-serif text-2xl text-inorbit-blue">₹{Number(r.refund_amount).toLocaleString('en-IN')}</div>
                                            <div className="text-[10px] uppercase tracking-widest text-text-tertiary">to be refunded</div>
                                        </div>
                                    </div>
                                    <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <div className="overline mb-1 text-[10px]">Items</div>
                                            <ul className="space-y-1">
                                                {(r.items || []).map((it, i) => (
                                                    <li key={i} className="text-text-secondary">• {it.name} <span className="text-text-tertiary">× {it.quantity}</span></li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <div className="overline mb-1 text-[10px]">Reason</div>
                                            <div className="text-text-secondary">{r.reason}</div>
                                            {r.notes && <div className="text-xs text-text-tertiary italic mt-1">"{r.notes}"</div>}
                                        </div>
                                    </div>
                                    {(r.status_history || []).length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-border-subtle flex flex-wrap gap-3 text-[11px] uppercase tracking-widest text-text-tertiary">
                                            {r.status_history.map((h, i) => (
                                                <span key={i}>
                                                    {STATUS_PILLS[h.status]?.label || h.status}
                                                    <span className="opacity-60"> · {new Date(h.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
